import { FormEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search as IconSearch } from 'react-feather';
import { Modal } from 'antd';
import dayjs from 'dayjs';

import { useLanguage } from '@languages/index';
import API from '@api/index';
import { queries } from '@api/api.constants';
import { CampaignDto, CloneToTemporaryCampaignRequestParams } from '@api/repositories/campaign/campaign.types';
import { GetGuidelineRulesRequestParams } from '@api/repositories/guidelineRules/guidelineRules.types';
import { BRAND_DTO_ENUMS } from '@api/repositories/brand/brand.constants';
import { GET_CAMPAIGNS_DTO_ENUMS } from '@api/repositories/campaign/campaign.constants';
import { GetCampaignManagersRequestParams } from '@api/repositories/campaignManager/campaignManager.types';
import GUIDELINE_RULES from '@constants/guidelineRules';
import LoadingText from '@components/loadingText';
import Button from '@components/button';
import Paginator from '@components/paginator';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import CampaignFormHeaderButton from '../campaignFormHeaderButton/campaignFormHeaderButton';

const CampaignFormPastCampaignsModal = () => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const queryClient = useQueryClient();
  const {
    modal,
    auth,
    brand,
    initializeCampaignFormData,
    setShowLoader,
    temporaryKey,
    refetchProducts,
    setIsInitializedGuidelineRules,
  } = useContext(CampaignFormContext);

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isCampaignFormPastCampaignsModalVisible, setIsCampaignFormPastCampaignsModalVisible] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>('');

  const getPastCampaignsParams = useMemo(() => {
    let params = {
      brandToken: auth.token,
      brandBusinessType: brand.businessType,
      mode: GET_CAMPAIGNS_DTO_ENUMS.MODE.PRODUCT_HIERARCHY,
      isCompleted: false,
      filterNotSelected: false,
      filterNotDeployed: false,
      order: GET_CAMPAIGNS_DTO_ENUMS.ORDER.CREATED_AT,
      page: currentPage,
      size: 20,
    };

    if (brand.businessType === BRAND_DTO_ENUMS.BUSINESS_TYPE.WHOLESALE) {
      params['outputTypeFl'] = GUIDELINE_RULES.OUTPUT_TYPES.PHOTO + ',0';
    }

    if (searchText) {
      params['productMasterName'] = searchText;
    }

    return params;
  }, [currentPage, searchText, brand.businessType]);

  // 과거 캠페인 조회
  const {
    data: getPastCampaignsData,
    isFetching,
    isFetched,
  } = useQuery({
    ...queries.campaign.getCampaigns(getPastCampaignsParams),
    enabled: isCampaignFormPastCampaignsModalVisible,
    placeholderData: keepPreviousData,
    select: (data) => data.data,
  });

  // 캠페인 임시저장으로 복제
  const { mutate: cloneToTemporaryCampaignMutate } = useMutation({
    mutationFn: (params: CloneToTemporaryCampaignRequestParams) => API.Campaign.cloneToTemporaryCampaign(params),
  });

  const toggleModal = useCallback(() => {
    setIsCampaignFormPastCampaignsModalVisible(
      (prevIsCampaignFormPastCampaignsModalVisible) => !prevIsCampaignFormPastCampaignsModalVisible,
    );
  }, []);

  const handleSearch = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSearchText(event.currentTarget?.inputSearch?.value?.trim() || '');
  }, []);

  const onSelectPastCampaign = useCallback(
    (pastCampaign: CampaignDto) => {
      setShowLoader(true);
      toggleModal();
      cloneToTemporaryCampaignMutate(
        {
          brandToken: auth.token,
          temporaryKey,
          campaignId: pastCampaign.id,
        },
        {
          onSuccess: () => {
            initializeCampaignFormData({ loadedCampaign: { ...pastCampaign } });
            setIsInitializedGuidelineRules(false);
            refetchProducts();
            // guidelineRules refetch
            queryClient.refetchQueries({
              queryKey: queries.guidelineRules.getGuidelineRules._def,
              predicate: (query) => {
                return (
                  (query.queryKey?.[2] as { params: GetGuidelineRulesRequestParams }).params?.temporaryKey ===
                  temporaryKey
                );
              },
            });
            // campaignManagers refetch
            queryClient.refetchQueries({
              queryKey: queries.campaignManager.getCampaignManagers._def,
              predicate: (query) => {
                return (
                  (query.queryKey?.[2] as { params: GetCampaignManagersRequestParams }).params?.temporaryKey ===
                  temporaryKey
                );
              },
            });
          },
          onError: (error: any) => {
            modal.showAlertModal({
              title: 'Error: createAndMountClone',
              text: error.message,
            });
          },
          onSettled: () => {
            setShowLoader(false);
          },
        },
      );
    },
    [initializeCampaignFormData, temporaryKey, auth.token],
  );

  useEffect(() => {
    if (isFetched) {
      setIsInitialized(true);
    }
  }, [isFetched]);

  return (
    <>
      <CampaignFormHeaderButton onClick={toggleModal}>
        {languageTexts.campaign_form_past_campaigns_modal_button}
      </CampaignFormHeaderButton>

      {isCampaignFormPastCampaignsModalVisible && (
        <Modal
          className="squared-antd-modal"
          style={{ padding: '40px 20px 40px 20px' }}
          width={400}
          zIndex={1090}
          centered={true}
          closable={false}
          footer={null}
          open={isCampaignFormPastCampaignsModalVisible}
          onCancel={toggleModal}
          afterClose={() => setSearchText('')}
        >
          <div className="flex flex-col justify-center align-center bg-white">
            <div className="content relative flex flex-col">
              {isFetching && (
                <>
                  {!isInitialized ? (
                    <LoadingText
                      text="Loading"
                      render={(text) => (
                        <div className="pt-8 text-center text-2xl font-bold text-gray-300 select-none">{text}</div>
                      )}
                    />
                  ) : (
                    <div className="absolute top-0 flex items-center justify-center w-full h-full bg-slate-400 opacity-50 z-[1]">
                      <LoadingText
                        text="Loading"
                        render={(text) => (
                          <div className="w-[180px] p-2 text-center text-2xl font-bold text-white select-none">
                            {text}
                          </div>
                        )}
                      />
                    </div>
                  )}
                </>
              )}

              {isInitialized && (
                <div>
                  {/* 과거 캠페인 검색 Form */}
                  <form className="p-[20px] flex items-center gap-[7px]" onSubmit={handleSearch}>
                    <button type="submit" disabled={isFetching}>
                      <IconSearch size={24} />
                    </button>
                    <input
                      className="text-[15px]"
                      type="text"
                      name="inputSearch"
                      placeholder={languageTexts.campaign_form_past_campaigns_modal_search_placeholder}
                    />
                  </form>

                  <div>
                    {/* 과거 캠페인 목록 */}
                    {!getPastCampaignsData?.campaigns || getPastCampaignsData.campaigns.length <= 0 ? (
                      <p className="p-5 text-center font-bold text-slate-500">
                        {languageTexts.campaign_form_past_campaigns_modal_campaigns_empty}
                      </p>
                    ) : (
                      <ul>
                        {getPastCampaignsData?.campaigns?.map((pastCampaign) => (
                          <li
                            key={`past-campaign-${pastCampaign.id}`}
                            data-campaign-id={pastCampaign.id}
                            className="p-[20px] pt-[15px] border-t border-[#E8E8E8]"
                          >
                            <div>
                              <div className="flex justify-between items-center gap-3">
                                <div className="text-[15px] font-bold line-clamp-1">
                                  {languageTexts.campaign_form_past_campaigns_modal_campaign_created_at(
                                    dayjs(pastCampaign.createdAt).format('YY.MM.DD'),
                                  )}
                                </div>
                                <div className="flex-none flex items-center">
                                  <button
                                    type="button"
                                    className="px-[15px] py-[7px] bg-white border border-[#E8E8E8] text-[13px]"
                                    onClick={() => onSelectPastCampaign(pastCampaign)}
                                  >
                                    {languageTexts.load}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {pastCampaign?.description && (
                              <div className="mt-[8px] text-[12px]">{pastCampaign.description}</div>
                            )}

                            {pastCampaign?.productMasters?.length > 0 && (
                              <div className="mt-[15px] flex flex-col gap-[12px]">
                                {pastCampaign?.productMasters?.map((productMaster) => (
                                  <dl
                                    className="p-[20px] border border-[#E8E8E8]"
                                    key={`past-campaign-product-master-${productMaster.id}`}
                                  >
                                    <dt className="text-[15px] font-bold line-clamp-1">{productMaster.name}</dt>
                                    {productMaster.products?.length > 0 && (
                                      <dd className="mt-[18px]">
                                        {productMaster.products.map((product) => {
                                          const productInfo = [
                                            product.colorName,
                                            brand.isCategoryBeauty ? languageTexts._product_qty(product.qty) : null,
                                            brand.isCategoryFashion ? languageTexts._clothes(product.qty) : null,
                                          ].filter(Boolean);

                                          return (
                                            <div
                                              className="flex items-center gap-[20px]"
                                              key={`past-campaign-product-${product.id}`}
                                            >
                                              <div className="flex-none">
                                                <img
                                                  src={product.thumbnailSmUrl}
                                                  alt={'product image'}
                                                  className="w-[48px] h-[48px]"
                                                />
                                              </div>
                                              {productInfo.length > 0 && (
                                                <div className="text-[15px]">{productInfo.join(' / ')}</div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </dd>
                                    )}
                                  </dl>
                                ))}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 페이지네이션 */}
            {getPastCampaignsData?.campaigns?.length > 0 && getPastCampaignsData?.paginator && (
              <Paginator
                currentPage={getPastCampaignsData?.paginator?.currentPage}
                lastPage={getPastCampaignsData?.paginator?.lastPage}
                onPageChange={(page) => setCurrentPage(page)}
                offset={5}
              />
            )}

            {/* 닫기 버튼 */}
            <div className="buttons p-[20px] flex flex-row gap-1.5">
              <Button
                className={'w-full py-[15px] flex justify-center'}
                textClassName={'text text-[15px] tracking-tighter text-center'}
                textColor={'black'}
                buttonColor={'white'}
                text={languageTexts.cancel}
                onClick={toggleModal}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CampaignFormPastCampaignsModal;
